import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { assignedActivityForClientSchema } from '../../interfaces/IAssignedActivityForClient';
import { requiredStringSchema, calendarDateField, tzHeadersSchema, sseQuerySchema } from '../schemas';

// ============================================================
// Param Schemas
// ============================================================

const projectParamsSchema = z.object({
	projectId: requiredStringSchema,
});

const activityParamsSchema = projectParamsSchema.extend({
	id: requiredStringSchema,
});

// ============================================================
// Query/Body Schemas
// ============================================================

const workplanRangeQuerySchema = z.object({
	fromDate: calendarDateField,
	toDate: calendarDateField,
	sortBy: requiredStringSchema,
});

const getActivitiesBySequenceItemIdQuerySchema = z.object({
	sequenceItemId: requiredStringSchema,
});

const getStreamActivitiesQuerySchema = sseQuerySchema.extend({
	projectId: requiredStringSchema,
	fromDate: calendarDateField,
	toDate: calendarDateField,
	sortBy: requiredStringSchema,
});

const getActivitiesEndDateBetweenDatesQuerySchema = z.object({
	dateFrom: calendarDateField,
	dateTo: calendarDateField,
});

const getTodayDangerousActivitiesQuerySchema = z.object({
	floorId: z.string().optional(),
});

const getActivitiesBetweenDatesQuerySchema = z.object({
	tz: z.string().optional(),
	dateFrom: calendarDateField,
	dateTo: calendarDateField,
});

const getDailyActivitiesQuerySchema = z.object({
	date: calendarDateField.optional(),
	floorId: z.string().optional(),
	professionId: z.string().optional(),
});

const createBulkActivitiesBodySchema = z.object({
	activities: z.array(z.any()),
	areaSequenceItems: z.array(z.any()),
	startDate: calendarDateField,
	endDate: calendarDateField,
});

const createActivityBodySchema = z.object({}).passthrough();

const updateTradeBodySchema = z.object({}).passthrough();

/**
 * Activity contract - 13 non-SSE routes registered via implementContract().
 *
 * IMPORTANT: Route definition order must match registration order.
 * /:id/mainPageStaticInfo MUST be before /:id to avoid route conflicts.
 */
export const activityContract = defineContract(
	'activities',
	{
		createBulkActivities: route({
			method: 'POST',
			path: '/projects/:projectId/activities/bulk',
			params: projectParamsSchema,
			body: createBulkActivitiesBodySchema,
			headers: tzHeadersSchema,
			response: z.array(z.any()),
			operationId: 'createBulkActivities',
			tags: ['Activities'],
			summary: 'Create bulk activities',
			description: 'Creates multiple activities in bulk with area sequence items',
		}),
		getActivitiesBySequenceItemId: route({
			method: 'GET',
			path: '/projects/:projectId/activities/byLinkedSequenceItemId',
			params: projectParamsSchema,
			query: getActivitiesBySequenceItemIdQuerySchema,
			response: z.array(z.any()),
			operationId: 'getActivitiesByLinkedSequenceItemId',
			tags: ['Activities'],
			summary: 'Get activities by sequence item ID',
			description: 'Retrieves all activities linked to a specific sequence item',
		}),
		/**
		 * Replaces the deleted `GET /allProjectsOngoingActivities`, which returned
		 * every enabled project's activities to any authenticated caller with no
		 * role gate. ReportsEngine's daily-summary interval was its one caller and
		 * now loops the projects it must serve, asking for one at a time — so the
		 * membership guard has a project to check and a failure is bounded to that
		 * project rather than starving every other project's summaries.
		 */
		getOngoingActivities: route({
			method: 'GET',
			path: '/projects/:projectId/activities/ongoing',
			params: projectParamsSchema,
			response: z.array(z.any()),
			operationId: 'getOngoingActivities',
			tags: ['Activities'],
			summary: 'Get ongoing activities of a project',
			description: 'Retrieves the assigned activities of the project ongoing activity groups',
		}),
		getTodayDangerousActivities: route({
			method: 'GET',
			path: '/projects/:projectId/activities/todayDangerous',
			params: projectParamsSchema,
			query: getTodayDangerousActivitiesQuerySchema,
			headers: tzHeadersSchema,
			response: z.array(z.any()),
			operationId: 'getTodayDangerousActivities',
			tags: ['Activities'],
			summary: 'Get today dangerous activities',
			description: 'Retrieves activities marked as dangerous for today',
		}),
		getActivitiesBetweenDates: route({
			method: 'GET',
			path: '/projects/:projectId/activities/betweenDates',
			params: projectParamsSchema,
			query: getActivitiesBetweenDatesQuerySchema,
			headers: tzHeadersSchema,
			response: z.record(z.string(), z.array(z.any())),
			operationId: 'getActivitiesBetweenDates',
			tags: ['Activities'],
			summary: 'Get activities between dates',
			description: 'Retrieves activities grouped by profession within the date range',
		}),
		getActivitiesEndDateBetweenDates: route({
			method: 'GET',
			path: '/projects/:projectId/activities/endDateBetweenDates',
			params: projectParamsSchema,
			query: getActivitiesEndDateBetweenDatesQuerySchema,
			headers: tzHeadersSchema,
			response: z.array(z.any()),
			operationId: 'getActivitiesEndDateBetweenDates',
			tags: ['Activities'],
			summary: 'Get activities ending between dates',
			description: 'Retrieves activities with end dates within the specified date range',
		}),
		getDailyActivities: route({
			method: 'GET',
			path: '/projects/:projectId/activities/daily',
			params: projectParamsSchema,
			query: getDailyActivitiesQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getDailyActivities',
			tags: ['Activities'],
			summary: 'Get daily activities',
			description: 'Retrieves activities for a specific date with optional floor/profession filters',
		}),
		getActivitiesV2: route({
			method: 'GET',
			path: '/projects/:projectId/activities/v2',
			params: projectParamsSchema,
			query: workplanRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getActivitiesV2',
			tags: ['Activities'],
			summary: 'Get activities for workplan (v2)',
			description: 'Retrieves activities for workplan display with v2 format',
		}),
		/**
		 * Org-level cascade, not project data: Entities calls this when a trade is
		 * renamed and every activity carrying it must follow, across every project.
		 * There is no single project to scope it by — see PROJECT_LESS_BY_DESIGN.
		 */
		updateTrade: route({
			method: 'PUT',
			path: '/updateTrade',
			body: updateTradeBodySchema,
			response: z.string(),
			operationId: 'updateActivitiesTrade',
			tags: ['Activities'],
			summary: 'Update activities trade',
			description: 'Updates the trade/profession for activities',
		}),
		getActivityMainPageStaticInfo: route({
			method: 'GET',
			path: '/projects/:projectId/activities/:id/mainPageStaticInfo',
			params: activityParamsSchema,
			response: z.any(),
			operationId: 'getActivityMainPageStaticInfo',
			tags: ['Activities'],
			summary: 'Get activity main page static info',
			description: 'Retrieves static information for activity display on main page reports',
		}),
		getActivityById: route({
			method: 'GET',
			path: '/projects/:projectId/activities/:id',
			params: activityParamsSchema,
			response: z.any(),
			operationId: 'getActivityById',
			tags: ['Activities'],
			summary: 'Get activity by ID',
			description: 'Retrieves a specific activity by its ID',
		}),
		createActivity: route({
			method: 'POST',
			path: '/projects/:projectId/activities',
			params: projectParamsSchema,
			body: createActivityBodySchema,
			headers: tzHeadersSchema,
			response: z.array(assignedActivityForClientSchema),
			operationId: 'createActivity',
			tags: ['Activities'],
			summary: 'Create assigned activity',
			description: 'Creates a new assigned activity',
		}),
		getActivities: route({
			method: 'GET',
			path: '/projects/:projectId/activities',
			params: projectParamsSchema,
			query: workplanRangeQuerySchema,
			headers: tzHeadersSchema,
			response: z.record(z.string(), z.array(z.array(assignedActivityForClientSchema))),
			operationId: 'getActivities',
			tags: ['Activities'],
			summary: 'Get activities in workplan format (legacy)',
			description: 'Retrieves activities grouped by section for workplan display',
		}),
	},
	{ root: true }
);

/**
 * SSE route for activity streaming - defined separately because SSE routes
 * cannot use implementContract() (they need access to fastify.sse, request.user, etc.)
 */
export const activityStreamRoute = route({
	method: 'GET',
	path: '/stream/v2',
	query: getStreamActivitiesQuerySchema,
	response: z.any(),
	operationId: 'getActivityStream',
	tags: ['Activities'],
	summary: 'Stream activities for workplan (SSE)',
	description: 'Server-Sent Events stream for real-time workplan activity updates',
});

export type GetStreamActivitiesQuery = z.infer<typeof getStreamActivitiesQuerySchema>;
